import { FC } from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

type ColumnItemProps = {
  id: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  onToggle: (key: string) => void;
  dragHandleProps?: React.HTMLAttributes<HTMLSpanElement>;
};

const ColumnItem: FC<ColumnItemProps> = ({
  id,
  label,
  checked,
  disabled = false,
  onToggle,
  dragHandleProps,
}) => {
  const hasDragHandle = Boolean(dragHandleProps);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: '0.5rem',
        py: '0.25rem',
        cursor: disabled ? 'default' : 'pointer',
        '&:hover': {
          backgroundColor: disabled ? 'transparent' : '#F5F6F7',
          '.drag-handle': {
            visibility: hasDragHandle ? 'visible' : 'hidden',
          },
        },
      }}
      onClick={() => !disabled && onToggle(id)}
    >
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={checked}
            disabled={disabled}
            onChange={() => !disabled && onToggle(id)}
            onClick={(e) => e.stopPropagation()}
            sx={{
              color: '#737F86',
              '&.Mui-checked': {
                color: '#2F736E',
              },
              '&.Mui-disabled': {
                color: '#2F736E',
                opacity: 0.5,
              },
              p: '4px',
            }}
            inputProps={{ 'aria-label': `Toggle column ${label}` }}
          />
        }
        label={
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: disabled ? '#737F86' : '#162936',
              lineHeight: 1.4,
              userSelect: 'none',
            }}
          >
            {label}
          </Typography>
        }
        sx={{ ml: 0, mr: 0, flex: 1 }}
      />
      <Box
        className="drag-handle"
        component="span"
        {...(hasDragHandle ? dragHandleProps : {})}
        sx={{
          display: 'flex',
          alignItems: 'center',
          visibility: 'hidden',
          cursor: hasDragHandle ? 'grab' : 'default',
          color: '#B0B8BF',
          mr: '2px',
          flexShrink: 0,
          '&:active': {
            cursor: 'grabbing',
            color: '#2F736E',
          },
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <DragIndicatorIcon sx={{ fontSize: '1rem' }} />
      </Box>
    </Box>
  );
};

export default ColumnItem;
export type { ColumnItemProps };
